import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { TaskViewDialog } from "../components/features/TaskViewDialog";
import { useAuth } from "../context/AuthContext";
import { mutations } from "../lib/supabase/mutations";
import { queries } from "../lib/supabase/queries";
import { Task, TaskQuote } from "../lib/types/database.types";
import { sendQuoteApprovedEmail } from "../utils/emailNotifications";

export const FinanceApprovalPage: React.FC = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [viewingTask, setViewingTask] = React.useState<Task | null>(null);
  const quotesQuery = useQuery({
    queryKey: ["task-quotes", "not-approved"],
    queryFn: () => queries.getTaskQuotesByStatus("not_approved"),
    enabled: Boolean(profile?.is_finance_approver),
  });
  const profilesQuery = useQuery({
    queryKey: ["profiles", "all"],
    queryFn: queries.getAllProfiles,
    enabled: Boolean(profile?.is_finance_approver),
  });
  const actionMutation = useMutation({
    mutationFn: async ({
      quote,
      approved,
    }: {
      quote: TaskQuote;
      approved: boolean;
    }) => {
      await mutations.updateTaskQuoteStatus(
        quote.id,
        approved ? "approved" : "rejected",
      );
      if (approved) {
        const task = quotesQuery.data?.find(
          (item) => item.quote.id === quote.id,
        )?.task;
        const updatedTask = await mutations.updateTask(quote.task_id, {
          status: "in_dev_progress",
        });
        const assignedProfile = profilesQuery.data?.find(
          (item) => item.id === updatedTask.assigned_to,
        );
        if (task && assignedProfile?.email) {
          await sendQuoteApprovedEmail(assignedProfile.email, updatedTask, {
            hours: Number(quote.hours),
            pricePerHourZar: Number(quote.price_per_hour_zar),
            totalZar: Number(quote.total_zar),
          });
        }
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["task-quotes"] });
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  if (!profile?.is_finance_approver)
    return <Navigate to="/dashboard" replace />;
  if (quotesQuery.isLoading || profilesQuery.isLoading)
    return <LoadingSpinner size="lg" text="Loading finance approvals..." />;
  if (quotesQuery.error || profilesQuery.error)
    return (
      <Card>
        <p className="text-red-700">Unable to load finance approvals.</p>
      </Card>
    );

  return (
    <div className="space-y-6">
      <Card
        header={
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Finance Approval
            </h1>
            <p className="text-sm text-slate-600">
              Review submitted quotes for all tasks.
            </p>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-stone-300 text-slate-600">
                <th className="px-3 py-3">Task</th>
                <th className="px-3 py-3">Hours</th>
                <th className="px-3 py-3">Rate</th>
                <th className="px-3 py-3">Total</th>
                <th className="px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(quotesQuery.data || []).map(({ task, quote }) => (
                <tr key={quote.id} className="border-b border-stone-200">
                  <td className="px-3 py-3 font-medium">{task.title}</td>
                  <td className="px-3 py-3">{quote.hours}</td>
                  <td className="px-3 py-3">
                    R {Number(quote.price_per_hour_zar).toFixed(2)} / hour
                  </td>
                  <td className="px-3 py-3 font-semibold">
                    R {Number(quote.total_zar).toFixed(2)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setViewingTask(task)}
                      >
                        View Task
                      </Button>
                      <Button
                        size="sm"
                        onClick={() =>
                          actionMutation.mutate({ quote, approved: true })
                        }
                        isLoading={actionMutation.isPending}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          actionMutation.mutate({ quote, approved: false })
                        }
                        isLoading={actionMutation.isPending}
                      >
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {quotesQuery.data?.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">
              No quotes are awaiting approval.
            </p>
          )}
        </div>
        {actionMutation.error && (
          <p className="mt-4 text-sm text-red-700">
            {(actionMutation.error as Error).message}
          </p>
        )}
      </Card>
      {viewingTask && (
        <TaskViewDialog
          task={viewingTask}
          onClose={() => setViewingTask(null)}
          assigneeLabel={
            profilesQuery.data?.find(
              (item) => item.id === viewingTask.assigned_to,
            )?.full_name || undefined
          }
        />
      )}
    </div>
  );
};
